import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { faAngleDown, faAngleUp, faSignOutAlt, faFilePdf, faComments, faUserGear } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Login from './Login';
import ExpandableAssistantLion from './ExpandableAssistantLion';
import ExpandableClient from './ExpandableClient';
import Expandable from './Expandable';
import ChatWidget from './ChatWidget';
import BreachesModal from './breachesModal';
import UserManagement from './UserManagement';
import { API_BASE_URL } from './auth';
import './App.css';
import { faSearchDollar, faCalendar, faChartLine, faGlobeAmericas, faMoneyCheckDollar, faSquareUpRight, faChartPie, faHandshake, faAddressBook, faChartSimple, faUsersLine } from '@fortawesome/free-solid-svg-icons';
import './translations';
import { useTranslation } from 'react-i18next';
import { removeToken } from './auth';

const SUMMARY_EXPANDABLES = {
  performance:          { titleKey: 'performance',                icon: faChartLine },
  region_allocation:    { titleKey: 'regionAllocation',           icon: faGlobeAmericas },
  assetclass_allocation:{ titleKey: 'assetClassAllocation',       icon: faCalendar },
  currency_allocation:  { titleKey: 'currencyAllocation',         icon: faSearchDollar },
  asset_under_mgt_mgr:  { titleKey: 'asset_under_management_mgr', icon: faMoneyCheckDollar },
  kpi_mgr:              { titleKey: 'kpi',                        icon: faChartSimple },
  net_new_asset_mgr:    { titleKey: 'net_new_asset_mgr',          icon: faSquareUpRight },
  income_information_mgr:{ titleKey: 'income_information_mgr',    icon: faChartPie },
  new_clients_mgr:      { titleKey: 'new_clients_mgr',            icon: faHandshake },
  asset_under_mgt_adv:  { titleKey: 'asset_under_management_adv', icon: faMoneyCheckDollar },
  net_new_asset_adv:    { titleKey: 'net_new_asset_adv',          icon: faSquareUpRight },
  new_clients_adv:      { titleKey: 'new_clients_adv',            icon: faHandshake },
  contacted_clients_adv:{ titleKey: 'client_contact',             icon: faAddressBook },
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [uiConfig, setUiConfig] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);
  const [expandedComponent, setExpandedComponent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [sessionId, setSessionId] = useState(null);
  const [chatPrefill, setChatPrefill] = useState('');
  const [showUserMgmt, setShowUserMgmt] = useState(false);

  const generateSessionId = () => {
    const timestamp = Date.now();
    const randomNumber = Math.floor(Math.random() * 100000) + 1;
    return `${timestamp}-${randomNumber}`;
  };

  useEffect(() => {
    setSessionId(generateSessionId());
  }, []);

  const resetSessionId = () => {
    setSessionId(generateSessionId());
  };

  const handleLogin = (authStatus, userData) => {
    setIsAuthenticated(authStatus);
    if (authStatus && userData) {
      setUsername(userData.username || '');
      setFirstName(userData.first_name || '');
      setUiConfig(userData.ui_config || {});
      setSessionId(generateSessionId());
    }
  };

  const handleUserUpdated = () => {
    const stored = localStorage.getItem('user_data');
    if (stored) {
      const u = JSON.parse(stored);
      setUsername(u.username || '');
      setFirstName(u.first_name || '');
      setUiConfig(u.ui_config || {});
    }
  };

  const toggleExpandables = () => {
    setAllExpanded(!allExpanded);
  };

  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    i18n.changeLanguage(newLanguage);
  };

  const handleModalOpen = () => setIsModalOpen(true);

  function downloadPDF() {
    const pdfUrl = uiConfig.pdf_url || '/executive_financial_dashboard.pdf';
    const downloadName = uiConfig.pdf_download_name || 'executive_financial_dashboard.pdf';

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  }

  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });

  function logout() {
    removeToken();
    i18n.changeLanguage('de');
    setIsAuthenticated(false);
    setUsername('');
    setFirstName('');
    setUiConfig({});
    setChatPrefill('');
    setAllExpanded(false);
    setExpandedComponent(null);
    setIsModalOpen(false);
    setSessionId(generateSessionId());
    setShowUserMgmt(false);
  }

  return (
    <Router>
      <div className="parent-container">
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                showUserMgmt ? (
                  <UserManagement onBack={() => setShowUserMgmt(false)} onUserUpdated={handleUserUpdated} />
                ) : (
                <div className="expandables-parent">
                  <div className="logout-container">
                    <img src={process.env.PUBLIC_URL + '/bank-logo.svg'} alt="login-logo" className="app-logo" />
                    <div>
                      <FontAwesomeIcon icon={faUserGear} onClick={() => setShowUserMgmt(true)} className="logout-icon" style={{ marginRight: 20 }} />
                      <FontAwesomeIcon icon={faSignOutAlt} onClick={logout} className="logout-icon" />
                    </div>
                  </div>
                  <div className="pdf-download-container">
                    {uiConfig.show_pdf_download && (
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        onClick={downloadPDF}
                        className="pdf-icon"
                      />
                    )}
                    {uiConfig.show_breaches_button && (
                      <button
                        variant="contained"
                        color="primary"
                        onClick={handleModalOpen}
                        className='breaches-button'
                      >
                        ⚠️
                      </button>
                    )}
                  </div>
                  <div className="language-selection-container">
                    <select
                      className="language-options"
                      name="languages"
                      id="language-select"
                      onChange={handleLanguageChange}
                    >
                      <option value="de">Deutsch</option>
                      <option value="en">English</option>
                      <option value="fr">Français</option>
                      <option value="es">Español</option>
                      <option value="it">Italiano</option>
                    </select>
                  </div>
                  <h1 className="app-title">Early Lion AI</h1>
                  <h2 className="app-sub-title">{t('greeting')} {firstName}!</h2>
                  <div className="open-all-button-container">
                    <div className="date-container">{today}</div>
                    <button className="open-all-button" onClick={toggleExpandables}>
                      {allExpanded ? t('closeAll') : t('openAll')} <FontAwesomeIcon icon={allExpanded ? faAngleUp : faAngleDown} />
                    </button>
                  </div>
                  {uiConfig.show_breaches_button && (
                    <BreachesModal
                      open={isModalOpen}
                      handleClose={() => setIsModalOpen(false)}
                      onExpandChat={(component, prefillText) => {
                        setExpandedComponent(component);
                        setChatPrefill(prefillText);
                        if (component === 'chat') {
                          window.scrollTo({
                            top: document.body.scrollHeight,
                            behavior: 'smooth',
                          });
                        }
                      }}
                    />
                  )}
                  {[...new Set((uiConfig.expandables || []).map(t => t === 'chat_audio' ? 'chat' : t))].map(type => {
                    // Summary-type expandables
                    const summaryCfg = SUMMARY_EXPANDABLES[type];
                    if (summaryCfg) {
                      return (
                        <Expandable
                          key={type}
                          title={t(summaryCfg.titleKey)}
                          summaryApiUrl={`${API_BASE_URL}/api/summary?summary_type=${type}&language=${i18n.language}`}
                          icon={summaryCfg.icon}
                          expanded={allExpanded}
                          language={i18n.language}
                        />
                      );
                    }
                    // Special component expandables
                    switch (type) {
                      case 'chat_widget':
                        return <ChatWidget key={type} language={i18n.language} themeColor="#19d0ef" hoverColor="#0595d3" />;
                      case 'chat':
                      case 'chat_audio':
                        return (
                          <ExpandableAssistantLion
                            key={type}
                            userName={username}
                            title={t('chat')}
                            icon={faComments}
                            expanded={expandedComponent === 'chat'}
                            sessionId={sessionId}
                            language={i18n}
                            resetSessionId={resetSessionId}
                            chatPrefill={chatPrefill}
                            onCloseChat={() => {
                              setExpandedComponent(null);
                              setChatPrefill('');
                            }}
                            onOpenChat={() => setExpandedComponent('chat')}
                            onResetMessages={() => setChatPrefill('')}
                          />
                        );
                      case 'client_notes':
                        return (
                          <ExpandableClient
                            key={type}
                            title={t('customer_note')}
                            icon={faUsersLine}
                            expanded={allExpanded}
                            sessionId={sessionId}
                            language={i18n}
                          />
                        );
                      default:
                        return null;
                    }
                  })}
                </div>                  )                ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </div>
    </Router>
    )
}

export default App;
